import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IcShieldCheck, IcLock } from '../theme/icons';
import authService from '../services/authService';
import { requestIdentity } from '../services/identityGate';
import { getManagerSession, startManagerSession, endManagerSession, onManagerSessionChange } from '../services/managerSession';

// Accès manager sur une CAISSE. Rien n'est visible pour le vendeur : c'est en
// ouvrant une page réservée (ReservedGate) qu'un manager / propriétaire se fait
// reconnaître — caméra d'abord, sans aperçu ; sinon son code. La caisse
// travaille alors sans limite quelques minutes (bandeau + « Verrouiller »).

// Re-rend le composant qui l'appelle quand le mode commence / finit.
export function useManagerSession() {
  const [session, setSession] = useState(() => getManagerSession());
  useEffect(() => onManagerSessionChange(() => setSession(getManagerSession())), []);
  return session;
}

// Écran ou page RÉSERVÉ(E) sur une caisse (owner 2026-09-30 : plus de bouton
// « Mode manager » — le vendeur n'a rien à voir) : en y entrant, la caméra
// cherche un manager / propriétaire (sans aperçu), sinon son code. Reconnu →
// la page s'ouvre, sans limite quelques minutes ; annulé → retour à la vente.
export const ReservedGate = ({ children }) => {
  const session = useManagerSession();
  const navigate = useNavigate();
  const asking = useRef(false);
  const open = authService.isAdmin();
  useEffect(() => {
    if (open || asking.current) return;
    asking.current = true;
    requestIdentity({
      minLevel: 'account',   // compte manager ou propriétaire — pas un chef d'équipe (retours seulement)
      title: 'Accès réservé',
      reason: 'Un compte manager ou le propriétaire : la caméra le reconnaît, sinon son code.',
    }).then((who) => {
      asking.current = false;
      if (who) startManagerSession(who);
      else navigate('/seller-sales', { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session]);
  return open ? children : null;
};

const mmss = (ms) => {
  const t = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

// Bandeau sous la barre du haut pendant le mode manager : qui, temps restant,
// « Terminer ». Il disparaît tout seul à la fin.
export const ManagerModeBar = () => {
  const session = useManagerSession();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!session) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [session]);
  if (!session || !authService.isCaisse()) return null;
  const who = session.who || {};
  return (
    <div role="status" aria-live="polite" className="bg-blue-50 text-blue-800 border-b border-blue-100">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <IcShieldCheck className="text-blue-600" />
        <span><span className="font-semibold">Accès manager</span> · {who.name || who.username}</span>
        <span className="tabular-nums text-blue-700">{mmss(session.until - Date.now())} restantes</span>
        <button type="button" onClick={endManagerSession}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-blue-800 hover:bg-blue-100 transition-colors">
          <IcLock /> Verrouiller
        </button>
      </div>
    </div>
  );
};
